import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import ListFilterBar from '../common/ListFilterBar';
import FilterFieldSelect from '../forms/FilterFieldSelect';
import ImageCard from '../../../shared/components/ImageCard';
import MediaImageCard from './MediaImageCard';

const SHOW_OPTIONS = [
  { value: 'all', label: 'All images' },
  { value: 'used', label: 'Used in workflows' },
  { value: 'unused', label: 'Unused' },
];

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
  { value: 'name', label: 'Name (A–Z)' },
];

function bySort(sort) {
  if (sort === 'name') return (a, b) => (a.filename || '').localeCompare(b.filename || '');
  const direction = sort === 'oldest' ? 1 : -1;
  return (a, b) => direction * ((new Date(a.created_at)) - (new Date(b.created_at)));
}

// The slot at the front of the grid. It is both a button and a drop target, because an image
// grid is the one place people expect to be able to drop a file.
function AddImagesCard({ onPickFiles, onUpload, disabled }) {
  const [over, setOver] = useState(false);

  const handleDrop = (event) => {
    event.preventDefault();
    setOver(false);
    if (disabled) return;
    const files = [...(event.dataTransfer?.files || [])].filter((f) => f.type.startsWith('image/'));
    if (files.length) onUpload(files);
  };

  return (
    <button
      type="button"
      className={`mntl-image-drop ${over ? 'mntl-image-drop--over' : ''}`}
      disabled={disabled}
      onClick={onPickFiles}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={handleDrop}
    >
      <i className="bi bi-plus-lg" aria-hidden />
      <span className="mntl-image-drop__title">Add images</span>
      <span className="mntl-image-drop__hint">or drag &amp; drop</span>
    </button>
  );
}

AddImagesCard.propTypes = {
  onPickFiles: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

AddImagesCard.defaultProps = { disabled: false };

export default function MediaLibraryGrid({
  items,
  loading,
  error,
  fetchDownloadUrl,
  onSelect,
  selectedId,
  onPickFiles,
  onUpload,
  uploading,
}) {
  const [query, setQuery] = useState('');
  const [show, setShow] = useState('all');
  const [sort, setSort] = useState('newest');

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((item) => (!q || (item.filename || '').toLowerCase().includes(q)))
      .filter((item) => {
        if (show === 'used') return (item.usage_count || 0) > 0;
        if (show === 'unused') return !item.usage_count;
        return true;
      })
      .sort(bySort(sort));
  }, [items, query, show, sort]);

  const filterExtras = (
    <>
      <FilterFieldSelect
        id="media-filter-show"
        label="Show"
        value={show}
        onChange={(e) => setShow(e.target.value)}
        options={SHOW_OPTIONS}
      />
      <FilterFieldSelect
        id="media-filter-sort"
        label="Sort"
        value={sort}
        onChange={(e) => setSort(e.target.value)}
        options={SORT_OPTIONS}
      />
    </>
  );

  return (
    <div>
      <ListFilterBar
        inputId="media-filter-search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search images…"
        extras={filterExtras}
        summary={`${visible.length} of ${items.length} image${items.length === 1 ? '' : 's'}`}
      />

      {error && <div className="alert alert-danger py-2 small">{error}</div>}

      <div className="d-flex flex-wrap align-items-stretch gap-3">
        {/* Always first, so the way to add an image is in the same place whether the library
            holds nothing or two hundred. */}
        <AddImagesCard onPickFiles={onPickFiles} onUpload={onUpload} disabled={uploading} />

        {loading && items.length === 0 && (
          <ImageCard title="Loading…" loading />
        )}

        {visible.map((item) => (
          <MediaImageCard
            key={item.id}
            item={item}
            fetchDownloadUrl={fetchDownloadUrl}
            selected={selectedId === item.id}
            onSelect={onSelect}
          />
        ))}
      </div>

      {!loading && items.length > 0 && visible.length === 0 && (
        <p className="text-muted small mt-3 mb-0">No images match these filters.</p>
      )}
    </div>
  );
}

MediaLibraryGrid.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  error: PropTypes.string,
  fetchDownloadUrl: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  selectedId: PropTypes.number,
  onPickFiles: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
};

MediaLibraryGrid.defaultProps = {
  loading: false,
  error: null,
  selectedId: null,
  uploading: false,
};
